/**
 * DepotDemandChart — Shows vehicles at depot per hour as a simple bar chart.
 *
 * Props:
 *   vehicles: array of extracted vehicles with depot_availability
 */
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

function timeToHour(timeStr) {
    if (!timeStr || timeStr === 'MISSING') return null;
    const [h] = timeStr.split(':').map(Number);
    return h;
}

function DepotDemandChart({ vehicles = [] }) {
    const hourlyCount = useMemo(() => {
        const counts = new Array(24).fill(0);

        vehicles.forEach((v) => {
            (v.depot_availability || []).forEach((window) => {
                const from = timeToHour(window.available_from);
                const until = timeToHour(window.available_until);
                if (from == null || until == null) return;

                // Count each hour the vehicle is at depot
                for (let h = from; h < until; h++) {
                    if (h >= 0 && h < 24) counts[h]++;
                }
            });
        });

        return counts;
    }, [vehicles]);

    const maxCount = Math.max(...hourlyCount, 1);

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: vortxColors.stone, mb: 0.5, display: 'block' }}>
                Vehicles at depot by hour
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 48, gap: '2px' }}>
                {hourlyCount.map((count, h) => (
                    <Box
                        key={h}
                        sx={{
                            flex: 1,
                            height: `${(count / maxCount) * 100}%`,
                            minHeight: count > 0 ? 4 : 1,
                            bgcolor: count > 0 ? 'rgba(178,140,80,0.5)' : 'rgba(178,140,80,0.1)',
                            borderRadius: '2px 2px 0 0',
                            transition: 'height 0.2s',
                            '&:hover': { bgcolor: vortxColors.gold },
                        }}
                        title={`${h}:00 — ${count} vehicles`}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: 9 }}>0:00</Typography>
                <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: 9 }}>12:00</Typography>
                <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: 9 }}>24:00</Typography>
            </Box>
        </Box>
    );
}

export default DepotDemandChart;
